// ---------------------------------------------------------------------------
// XHR prototype patch — CSRF protection + fetch batcher + response cache
//
// reactive-json bundles its own axios instance (not a shared singleton), so
// an axios interceptor cannot be used. Patching XMLHttpRequest.prototype
// intercepts ALL XHR regardless of which library originated the request.
//
// Patches installed by installXhrPatch():
//
//   1. open()  — records the URL (unavailable in send()), sets credentials
//                for Drupal API routes, and flags batchable / cacheable GETs.
//
//   2. send()  — priority order:
//                a. Cacheable GET: async cache lookup.
//                   HIT  → replay synthetic response (resolveXhr) immediately.
//                   MISS → attach a load listener to populate cache on success,
//                          then continue with (b) or (c).
//                b. Batchable request: hold XHR and delegate to fetch batcher
//                   (which resolves it via /subrequests).
//                c. Mutating Drupal API request: inject CSRF token, then send.
//                d. Everything else: pass through to native send.
// ---------------------------------------------------------------------------

import { ensureCsrfToken, MUTATING_METHODS, isDrupalApiUrl } from './csrf.js';
import { isBatchableRequest, enqueueXhr, resolveXhr } from './requestBatcher.js';
import { isEligible, getXhrCache, putXhrCache, maybeInvalidateOnMutation } from './requestCache.js';

export function installXhrPatch() {
  const _xhrOpen = XMLHttpRequest.prototype.open;
  const _xhrSend = XMLHttpRequest.prototype.send;

  // Expose the original send so requestBatcher's pass-through path can call it
  // after clearing the _batchable flag on a lone-request optimisation.
  XMLHttpRequest.prototype._batchSend = _xhrSend;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (isDrupalApiUrl(url)) {
      this.withCredentials = true;
      this._drupalApi = true;
      this._httpMethod = method;
      this._drupalApiUrl = url;
    }

    // XHR batching: the URL is only available at open() time — send() only
    // receives an optional request body. We capture it here and set _batchable
    // so the send() patch below knows to hand this XHR to the batcher.
    if (isBatchableRequest(url, method)) {
      this._batchable = true;
      this._batchUrl = url;
    }

    // Response cache: flag eligible GET requests for cache lookup in send().
    if (isEligible(url, method)) {
      this._cacheable = true;
      this._cacheUrl = url;
    }

    return _xhrOpen.call(this, method, url, ...rest);
  };

  // ---------------------------------------------------------------------------
  // Shared "continue sending" logic — called after a cache miss.
  // Handles the batcher → CSRF → native cascade.
  // ---------------------------------------------------------------------------
  function continueXhrSend(xhr, body) {
    if (xhr._batchable) {
      enqueueXhr(xhr, xhr._batchUrl, body);
      return;
    }

    if (xhr._drupalApi && MUTATING_METHODS.includes((xhr._httpMethod || '').toUpperCase())) {
      ensureCsrfToken().then(token => {
        if (token) {
          try {
            xhr.setRequestHeader('X-CSRF-Token', token);
          }
          catch (_) {
            // Ignore state errors.
          }
        }
        _xhrSend.call(xhr, body);
      });
      return;
    }

    _xhrSend.call(xhr, body);
  }

  XMLHttpRequest.prototype.send = function (body) {
    // Cache invalidation on mutations: any successful POST/PUT/PATCH/DELETE
    // against a cacheable Drupal API namespace (e.g. /decoupled-api/) must
    // wipe the cache, otherwise the immediately-following collection GET
    // returns the stale list and the new entity is invisible until reload.
    // We attach this listener for every Drupal API mutation, not only cacheable
    // ones, because the URL written (/decoupled-api/save?...) and the URL read
    // (/decoupled-api/node/...) differ — maybeInvalidateOnMutation checks the
    // namespace prefix itself.
    if (this._drupalApi
        && MUTATING_METHODS.includes((this._httpMethod || '').toUpperCase())) {
      const xhr = this;
      const url = this._drupalApiUrl;
      const method = this._httpMethod;
      xhr.addEventListener('load', function onMutationLoad() {
        if (xhr.status >= 200 && xhr.status < 300) {
          maybeInvalidateOnMutation(url, method);
        }
        xhr.removeEventListener('load', onMutationLoad);
      });
    }

    // (a) Cache path: for eligible GET requests, check the cache first.
    // getXhrCache is async (SHA-256), so we return early and resume in .then().
    if (this._cacheable) {
      const xhr = this;
      const url = this._cacheUrl;

      getXhrCache(url).then(cachedPart => {
        if (cachedPart) {
          // Cache hit: replay response immediately without touching the network.
          resolveXhr(xhr, cachedPart);
          return;
        }

        // Cache miss: attach a load listener to populate cache when the real
        // response arrives, then let the normal batcher/CSRF/native flow run.
        // The listener works for both native XHR and batcher-synthesised XHR
        // because the batcher overrides getAllResponseHeaders / responseText on
        // the instance before firing the load event.
        xhr.addEventListener('load', function onLoad() {
          const status = xhr.status;
          if (status >= 200 && status < 300) {
            const headersStr = xhr.getAllResponseHeaders();
            const headersMap = { status: [String(status)] };
            if (headersStr) {
              for (const line of headersStr.split('\r\n')) {
                const colonIdx = line.indexOf(': ');
                if (colonIdx > 0) {
                  const name = line.slice(0, colonIdx).toLowerCase();
                  headersMap[name] = [line.slice(colonIdx + 2)];
                }
              }
            }
            putXhrCache(url, { headers: headersMap, body: xhr.responseText }); // fire-and-forget
          }
          xhr.removeEventListener('load', onLoad);
        });

        continueXhrSend(xhr, body);
      });

      return; // do not call native send synchronously
    }

    // (b–d) Non-cacheable: batcher / CSRF / native.
    continueXhrSend(this, body);
  };
}
